import React from 'react';
import { Loader2 } from 'lucide-react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'success' | 'outline' | 'danger' | 'destructive';
type Size = 'xs' | 'sm' | 'md' | 'lg';

function clsx(...parts: (string | false | null | undefined)[]) {
  return parts.filter(Boolean).join(' ');
}

const base =
  'inline-flex items-center justify-center gap-2 font-medium rounded-xl transition ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const variants: Record<Variant, string> = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700 focus-visible:ring-brand-500 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200',
  secondary: 'bg-gray-900 text-white hover:bg-black/90 focus-visible:ring-gray-900 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-white',
  ghost: 'bg-transparent text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800',
  success: 'bg-emerald-600 text-white hover:bg-emerald-700 focus-visible:ring-emerald-500 dark:bg-emerald-500 dark:text-gray-900 dark:hover:bg-emerald-400',
  outline: 'border border-gray-200 text-gray-700 hover:bg-gray-50 focus-visible:ring-brand-500 dark:border-gray-800 dark:text-gray-200 dark:hover:bg-gray-900/60',
  danger: 'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500',
  destructive: 'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500',
};

const sizes: Record<Size, string> = {
  xs: 'px-2.5 py-1 text-xs',
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm',
  lg: 'px-5 py-2.5 text-base',
};

type Props = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
};

export const Button: React.FC<Props> = ({ variant = 'primary', size = 'md', loading, className, children, ...rest }) => {
  return (
    <button className={clsx(base, variants[variant], sizes[size], className)} disabled={loading || rest.disabled} {...rest}>
      {loading ? <Loader2 className="animate-spin" size={16} /> : null}
      {/* inline-flex so an icon + label sit side by side; Tailwind Preflight makes
          <svg> display:block, which would otherwise stack the icon above the text */}
      <span className="inline-flex items-center justify-center gap-1.5">{children}</span>
    </button>
  );
};
